<script setup>
import { computed, ref } from 'vue';
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import { useI18n } from 'vue-i18n';

import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import ContactPicker from 'dashboard/components-next/CRM/Documents/ContactPicker/ContactPicker.vue';
import { DOCUMENT_TYPE_KEYS } from '../constants';
import { useDocumentTypesStore } from 'dashboard/stores/documentTypes';

const props = defineProps({ isSubmitting: { type: Boolean, default: false } });
const emit = defineEmits(['create']);
const { t } = useI18n();
const documentTypesStore = useDocumentTypesStore();

const dialogRef = ref(null);
const dialogSession = ref(0);
const contactPickerRef = ref(null);
const file = ref(null);
const selectedContact = ref(null);
const form = ref({
  title: '',
  documentType: '',
  contactId: '',
  dueDate: '',
  notes: '',
});

const rules = {
  title: { required },
  documentType: { required },
};

const v$ = useVuelidate(rules, form);

// Types are account-configurable data now; use the server labels when the
// list loads and fall back to the built-in keys so the dialog never breaks.
const typeOptions = computed(() => {
  const activeTypes = documentTypesStore.getActiveDocumentTypes;
  if (activeTypes.length) {
    return activeTypes.map(type => ({ value: type.key, label: type.label }));
  }
  return DOCUMENT_TYPE_KEYS.map(value => ({
    value,
    label: t(`DOCUMENTS.TYPES.${value.toUpperCase()}`),
  }));
});

const reset = () => {
  form.value = {
    title: '',
    documentType: '',
    contactId: '',
    dueDate: '',
    notes: '',
  };
  file.value = null;
  selectedContact.value = null;
  v$.value.$reset();
};

const ensureTypesLoaded = async () => {
  try {
    await documentTypesStore.get();
  } catch {
    // The built-in fallback options above keep the dialog usable.
  }
};

const open = () => {
  reset();
  // Remount the file input so a previously chosen file name is cleared.
  dialogSession.value += 1;
  ensureTypesLoaded();
  contactPickerRef.value?.load();
  dialogRef.value?.open();
};

const close = () => dialogRef.value?.close();

const selectFile = event => {
  [file.value] = event.target.files || [];
};

const selectContact = contact => {
  selectedContact.value = contact;
};

const submit = async () => {
  if (props.isSubmitting) return;

  const isValid = await v$.value.$validate();
  if (!isValid) return;

  const payload = new FormData();
  payload.append('document[title]', form.value.title.trim());
  payload.append('document[document_type]', form.value.documentType);
  if (form.value.contactId && selectedContact.value) {
    // Link the document to the picked contact and mirror its name onto the
    // party field so list rows keep a readable label.
    payload.append(
      'document[party_name]',
      selectedContact.value.name || selectedContact.value.email || ''
    );
    payload.append('document[links_attributes][][record_kind]', 'contact');
    payload.append('document[links_attributes][][record_id]', form.value.contactId);
    payload.append('document[links_attributes][][purpose]', 'party');
  }
  if (form.value.dueDate) payload.append('document[due_date]', form.value.dueDate);
  if (form.value.notes) payload.append('document[notes]', form.value.notes.trim());
  if (file.value) payload.append('document[file]', file.value);

  emit('create', payload);
};

defineExpose({ open, close });
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="t('DOCUMENTS.CREATE.TITLE')"
    :description="t('DOCUMENTS.CREATE.DESCRIPTION')"
    width="2xl"
    :persistent="isSubmitting"
  >
    <div class="grid gap-4 py-4 sm:grid-cols-2">
      <div class="sm:col-span-2">
        <Input
          v-model="form.title"
          :label="t('DOCUMENTS.FORM.TITLE')"
          :placeholder="t('DOCUMENTS.FORM.TITLE_PLACEHOLDER')"
          autofocus
        />
        <span v-if="v$.title.$error" class="text-xs text-n-ruby-11 mt-1 block">
          {{ t('DOCUMENTS.FORM.TITLE_REQUIRED') }}
        </span>
      </div>
      <div>
        <label class="block text-sm font-medium text-n-slate-12 mb-1.5">
          {{ t('DOCUMENTS.FORM.TYPE') }}
        </label>
        <ComboBox
          v-model="form.documentType"
          :options="typeOptions"
          :placeholder="t('DOCUMENTS.FORM.TYPE_PLACEHOLDER')"
          :has-error="v$.documentType.$error"
        />
        <span v-if="v$.documentType.$error" class="text-xs text-n-ruby-11 mt-1 block">
          {{ t('DOCUMENTS.FORM.TYPE_REQUIRED') }}
        </span>
      </div>
      <ContactPicker
        ref="contactPickerRef"
        v-model="form.contactId"
        :label="t('DOCUMENTS.FORM.PARTY')"
        :placeholder="t('DOCUMENTS.FORM.PARTY_PLACEHOLDER')"
        @select="selectContact"
      />
      <Input v-model="form.dueDate" type="date" :label="t('DOCUMENTS.FORM.DUE_DATE')" />
      <Input
        :key="dialogSession"
        class="sm:col-span-2"
        type="file"
        :label="t('DOCUMENTS.FORM.FILE')"
        accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
        @input="selectFile"
      />
      <TextArea
        v-model="form.notes"
        class="sm:col-span-2"
        :label="t('DOCUMENTS.FORM.NOTES')"
        :placeholder="t('DOCUMENTS.FORM.NOTES_PLACEHOLDER')"
      />
      <p class="sm:col-span-2 text-xs text-n-slate-10">
        {{ file ? t('DOCUMENTS.CREATE.RECEIVED_HINT') : t('DOCUMENTS.CREATE.REQUESTED_HINT') }}
      </p>
    </div>

    <template #footer>
      <div class="flex justify-end gap-2">
        <Button
          :label="t('DOCUMENTS.CANCEL')"
          ghost
          slate
          :disabled="isSubmitting"
          @click="close"
        />
        <Button
          :label="t('DOCUMENTS.CREATE.SUBMIT')"
          :is-loading="isSubmitting"
          :disabled="isSubmitting"
          @click="submit"
        />
      </div>
    </template>
  </Dialog>
</template>
